'use client';

import { Check, Copy, KeyRound, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../atoms/button/button';
import { DetailField, DetailTextInput } from '../../molecules/detail-fields/detail-fields';

export interface GatewayCredentialDialogViewProps {
	/** Codice del misuratore per cui si sta registrando il gateway. */
	meterCodice: string;
	/** Valore corrente del campo seriale dispositivo. */
	deviceId: string;
	/** Chiamata a ogni digitazione nel campo seriale dispositivo. */
	onDeviceIdChange: (v: string) => void;
	/** Secret monouso restituito dalla registrazione; se presente, il dialog passa alla modalità copia. */
	secret: string | null;
	/** Se true, la registrazione è in corso (disabilita il bottone, mostra "Registrazione…"). */
	busy: boolean;
	/** Se true, il secret è stato appena copiato (mostra l'icona di spunta). */
	copied: boolean;
	/** Chiamata al click su "Genera secret". */
	onRegister: () => void;
	/** Chiamata al click sull'icona di copia del secret. */
	onCopySecret: () => void;
	/** Chiamata alla chiusura del dialog. */
	onClose: () => void;
}

/**
 * Dialog per registrare o ruotare il secret di un gateway hardware collegato a un misuratore.
 * Il secret viene mostrato una sola volta perché l'operatore lo copi e lo configuri sul
 * dispositivo; tutto lo stato (form, esito, copia) è gestito dal chiamante.
 */
const GatewayCredentialDialogView = ({
	meterCodice,
	deviceId,
	onDeviceIdChange,
	secret,
	busy,
	copied,
	onRegister,
	onCopySecret,
	onClose,
}: GatewayCredentialDialogViewProps) => (
	<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
		<div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl">
			<div className="mb-4 flex items-center justify-between">
				<div className="flex items-center gap-2">
					<KeyRound className="h-4 w-4 text-accent" />
					<h2 className="text-sm font-bold text-text">Gateway hardware · {meterCodice}</h2>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Chiudi"
					className="text-text-muted hover:text-text"
				>
					<X className="h-4 w-4" />
				</button>
			</div>

			{!secret ? (
				<div className="space-y-3">
					<p className="text-[13px] text-text-muted">
						Registra il gateway che legge questo misuratore. Riceverai un secret da configurare sul
						dispositivo per inviare le letture a <code>/api/gateway/readings</code>.
					</p>
					<DetailField label="Seriale dispositivo">
						<DetailTextInput
							value={deviceId}
							onChange={onDeviceIdChange}
							placeholder="es. SGW-00A1B2C3"
						/>
					</DetailField>
					<div className="flex justify-end gap-2 pt-1">
						<Button type="button" variant="outline" size="sm" onClick={onClose}>
							Annulla
						</Button>
						<Button type="button" variant="accent" size="sm" onClick={onRegister} disabled={busy}>
							{busy ? 'Registrazione…' : 'Genera secret'}
						</Button>
					</div>
				</div>
			) : (
				<div className="space-y-3">
					<p className="text-[13px] text-warn">
						⚠ Copia il secret adesso: non sarà più visibile dopo la chiusura.
					</p>
					<div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
						<code className="flex-1 break-all font-mono text-[13px] text-text">{secret}</code>
						<button
							type="button"
							onClick={onCopySecret}
							aria-label="Copia secret"
							className="shrink-0 text-text-muted hover:text-accent"
						>
							{copied ? (
								<Check className="h-4 w-4 text-accent-green" />
							) : (
								<Copy className="h-4 w-4" />
							)}
						</button>
					</div>
					<div className="flex justify-end pt-1">
						<Button type="button" variant="accent" size="sm" onClick={onClose}>
							Fatto
						</Button>
					</div>
				</div>
			)}
		</div>
	</div>
);
GatewayCredentialDialogView.displayName = 'GatewayCredentialDialogView';

export { GatewayCredentialDialogView };
